import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import { delay } from "lodash";

import CustomSnapLineMode from "./snap/CustomSnapLineMode";
import { removeGuides } from "./helper";

const { constants: Constants } = MapboxDraw;

const drawLineString: DrawCustomMode = {
  ...CustomSnapLineMode,

  onSetup: function (opts) {
    const state = CustomSnapLineMode.onSetup?.call(this, opts);
    state.midPoints = this._ctx.options.midPoints !== false; // default to true if not set
    // Determine whether to allow multiple line strings to be drawn in a single session based on the multiCreate option
    state.multiCreate = opts.multiCreate ?? true;
    removeGuides.call(this);

    // Add contextmenu listener to prevent browser context menu
    const canvas = this.map.getCanvas();
    state.contextMenuHandler = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };
    canvas.addEventListener("contextmenu", state.contextMenuHandler);

    return state;
  },

  onClick: function (state, e) {
    CustomSnapLineMode.onClick?.call(this, state, e);

    // If midpoints are disabled, we want to exit the mode after drawing a line with 2 points (i.e. a single segment)
    if (!state.midPoints && state.line.coordinates.length === 3) {
      if (!state.multiCreate) {
        this.changeMode(Constants.modes.SIMPLE_SELECT, { featureIds: [state.line.id] });
      } else {
        // Stay in draw mode for multiple line creation
        this.changeMode(Constants.modes.DRAW_LINE_STRING);
      }
    }
  },

  onStop: function (state) {
    // Clean up the contextmenu listener when exiting the mode
    if (state.contextMenuHandler) {
      const canvas = this.map.getCanvas();
      canvas.removeEventListener("contextmenu", state.contextMenuHandler);
    }

    const lineCoordinates = state.line?.coordinates?.length || 0;

    // Check if a valid line will be created after the parent removes the ghost coordinate
    // The line needs >= 3 coordinates now (2 real points + 1 ghost) to be valid after removal
    // If the line was cancelled (Escape), it would have fewer coordinates
    // Child modes can set preventMultiCreate to true to override multiCreate behavior
    const lineWasCompleted = lineCoordinates >= 3;
    const shouldContinueDrawing = state.multiCreate && lineWasCompleted && !state.preventMultiCreate;

    // Always call parent onStop to clean up (this removes the ghost coordinate and fires CREATE event if valid)
    CustomSnapLineMode.onStop?.call(this, state);

    // If multiCreate is enabled and a valid line was created, restart draw mode
    if (shouldContinueDrawing) {
      // Use delay to ensure the mode change happens after the current mode has fully stopped
      delay(() => {
        this.changeMode(Constants.modes.DRAW_LINE_STRING);
      }, 0);
    }
  },

  onMouseDown: function (state, e) {
    // Check for right-click (button 2)
    if (e.originalEvent?.button === 2) {
      // Complete the line if we have at least 2 points
      if (state.line.coordinates.length >= 2) {
        // Use delay to allow contextmenu event to be prevented first
        delay(() => {
          this.changeMode(Constants.modes.SIMPLE_SELECT, { featureIds: [state.line.id] });
        }, 0);
        return;
      }
    }

    // Call the parent mode's onMouseDown for normal left-click behavior
    CustomSnapLineMode.onMouseDown?.call(this, state, e);
  },
};

export default drawLineString;
